import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage, t } from '../utils/i18n';
import { glassStyles } from '../utils/glassStyles';

const ShoppingCart = ({ isOpen, onClose, items, onUpdateQuantity, onRemoveItem, onCheckout }) => {
  const { language } = useLanguage();
  
  if (!isOpen) return null;
  
  const calculateTotal = () => {
    return items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };
  
  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      
      <motion.div 
        className="absolute inset-y-0 right-0 max-w-md w-full text-white flex flex-col"
        style={glassStyles.static}
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      >
        <div className="flex justify-between items-center p-4 border-b border-white/20">
          <h2 className="text-xl font-bold">{t('cart.title', language.code)}</h2>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {items.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-white/80">{t('cart.empty', language.code)}</p>
              <button 
                onClick={onClose}
                className="mt-4 text-white hover:text-white/80"
                style={glassStyles.button}
              >
                {t('cart.continueShopping', language.code)}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map(item => (
                <motion.div 
                  key={item.id} 
                  className="flex border-b border-white/20 pb-4"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="w-20 h-20 rounded overflow-hidden" style={glassStyles.card}>
                    {item.image ? (
                      <img 
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-xs text-white/80">{t('product.noImage', language.code)}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="ml-4 flex-1">
                    <h3 className="text-sm font-medium">{item.name}</h3>
                    <p className="text-xs text-white/80">{item.material}</p>
                    <div className="mt-2 flex justify-between items-center">
                      <div className="flex items-center rounded" style={glassStyles.button}>
                        <motion.button 
                          onClick={() => onUpdateQuantity(item.id, Math.max(1, item.quantity - 1))}
                          className="px-2 py-1 text-white/80 hover:text-white"
                          whileTap={{ scale: 0.9 }}
                        >
                          -
                        </motion.button>
                        <span className="px-2 py-1">{item.quantity}</span>
                        <motion.button 
                          onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                          className={`px-2 py-1 ${(item.stock && item.quantity >= item.stock) ? 'text-white/40 cursor-not-allowed' : 'text-white/80 hover:text-white'}`}
                          disabled={item.stock && item.quantity >= item.stock}
                          whileTap={{ scale: 0.9 }}
                        >
                          +
                        </motion.button>
                      </div>
                      <motion.button 
                        onClick={() => onRemoveItem(item.id)}
                        className="text-red-400 hover:text-red-300"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </motion.button>
                    </div>
                    <div className="mt-1 text-right">
                      <span className="font-medium">${(item.price * item.quantity).toLocaleString()}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
        
        {items.length > 0 && (
          <div className="p-4 border-t border-white/20">
            <div className="flex justify-between mb-4">
              <span className="font-medium">{t('cart.total', language.code)}</span>
              <span className="font-bold">${calculateTotal().toLocaleString()}</span>
            </div>
            <motion.button 
              onClick={onCheckout}
              className="w-full py-2 rounded-md text-white"
              style={glassStyles.button}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {t('cart.checkout', language.code)}
            </motion.button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ShoppingCart;
